/**
 * Central safe gate for every service-worker activation / page reload.
 *
 * Explicit async lifecycle, one path for every trigger (version mismatch,
 * waiting worker, controllerchange, peer tab, broken chunk, route change):
 *
 *   1. Target = a REAL build id from /version.json (never a placeholder;
 *      peer/controllerchange/chunk hints are verified by fetching it).
 *   2. Defer (notify once per target) while a protected route, draft or dirty
 *      form is active.
 *   3. If a new worker is installing → await "installed" (bounded).
 *      If a worker is waiting → listen for controllerchange FIRST, then send
 *      SKIP_WAITING, then await control (bounded). Timeout → no reload; the
 *      update stays pending and is retried on the next trigger.
 *      No worker / already current → network-first navigation suffices.
 *   4. Re-check safety immediately before reloading (the user may have typed
 *      while activation was in flight).
 *   5. Only now: record the target in the durable per-target guard, announce
 *      it to peers, and perform the ONE reload.
 */

import { hasReloadedFor, markReloadedFor, shouldDeferUpdate } from "./pwaUpdate";

export type WorkerStatus = "none" | "installing" | "waiting" | "current";

export interface WorkerControl {
  /** Registration state (implementations may run reg.update() first). */
  status(): Promise<WorkerStatus>;
  /** Resolve true once the installing worker reaches installed/activated. */
  waitUntilInstalled(timeoutMs: number): Promise<boolean>;
  /** Resolve true on the next controllerchange; false on timeout. */
  waitForControllerChange(timeoutMs: number): Promise<boolean>;
  /** Ask the waiting worker to activate (SKIP_WAITING message). */
  requestActivation(): Promise<void>;
}

export interface CoordinatorDeps {
  getPathname: () => string;
  getStoredItem: (key: string) => string | null;
  isFormDirty: () => boolean;
  reload: () => void;
  /** Real deployed build id from /version.json, or null on any failure. */
  fetchLatestBuildId: () => Promise<string | null>;
  /** Null when no service worker is in use (still refreshes via network). */
  worker: WorkerControl | null;
  /** Announce to peer tabs which build this tab is reloading into. */
  announce: (buildId: string) => void;
  notify: (message: {
    title: string;
    description?: string;
    actionLabel?: string;
    onAction?: () => void;
  }) => void;
  /** sessionStorage-like durable guard store. */
  storage: Pick<Storage, "getItem" | "setItem">;
  currentBuildId: string;
  installTimeoutMs?: number;
  activationTimeoutMs?: number;
}

export class UpdateCoordinator {
  private pendingTarget: string | null = null;
  private applying: Promise<void> | null = null;
  private resolving: Promise<void> | null = null;
  private notifiedFor: string | null = null;
  private reloaded = false;

  constructor(private deps: CoordinatorDeps) {}

  get pending(): string | null {
    return this.pendingTarget;
  }

  isDeferred(): boolean {
    return (
      this.deps.isFormDirty() ||
      shouldDeferUpdate(this.deps.getPathname(), this.deps.getStoredItem)
    );
  }

  /** A verified build id (from /version.json) differs from the running one. */
  onUpdateAvailable(buildId: string | null): Promise<void> {
    if (!buildId || buildId === this.deps.currentBuildId) return Promise.resolve();
    this.pendingTarget = buildId;
    return this.tryApply();
  }

  /** Peer tab announced a reload. Its id is only a hint: ignore our own build,
   *  then verify against the real deployed id (fake ids never trigger). */
  onPeerReloading(claimed: unknown): Promise<void> {
    if (typeof claimed !== "string" || !claimed || claimed === this.deps.currentBuildId) {
      return Promise.resolve();
    }
    return this.resolveLatest();
  }

  /** A new worker took control. During our own activation this is expected
   *  and ignored; otherwise fetch the real target if none is pending. */
  onControllerChange(): Promise<void> {
    if (this.applying) return Promise.resolve();
    if (this.pendingTarget) return this.tryApply();
    return this.resolveLatest();
  }

  /** A new worker finished installing and is waiting. If this page already
   *  runs the deployed build (e.g. a full navigation fetched the new HTML
   *  through the old worker's network-first route), activate the worker with
   *  NO reload — the page code is already current. Otherwise use the gate. */
  async onWorkerWaiting(): Promise<void> {
    const latest = await this.deps.fetchLatestBuildId();
    if (!latest) return;
    if (latest === this.deps.currentBuildId) {
      await this.deps.worker?.requestActivation();
      return;
    }
    await this.onUpdateAvailable(latest);
  }

  /** Old HTML referenced a chunk that no longer exists: reload only if a newer
   *  build is really deployed (no synthetic reasons, no same-build reloads). */
  onChunkError(): Promise<void> {
    return this.resolveLatest();
  }

  /** SPA route change / focus / visibility: retry a deferred update. */
  onRouteChange(): Promise<void> {
    return this.tryApply();
  }

  tryApply(): Promise<void> {
    if (!this.pendingTarget || this.reloaded) return Promise.resolve();
    if (this.applying) return this.applying;
    this.applying = this.apply().finally(() => {
      this.applying = null;
    });
    return this.applying;
  }

  private resolveLatest(): Promise<void> {
    if (this.resolving) return this.resolving;
    this.resolving = (async () => {
      const latest = await this.deps.fetchLatestBuildId();
      if (latest && latest !== this.deps.currentBuildId) await this.onUpdateAvailable(latest);
    })().finally(() => {
      this.resolving = null;
    });
    return this.resolving;
  }

  private deferIfUnsafe(target: string): boolean {
    if (!this.isDeferred()) return false;
    if (this.notifiedFor !== target) {
      this.notifiedFor = target;
      this.deps.notify({
        title: "A new version of ANAYA is ready.",
        description: "Your current work is safe — finish or discard this step, then update.",
        actionLabel: "Update now",
        // Re-enters the same gate: while work is unsaved it explains again.
        onAction: () => {
          this.notifiedFor = null;
          void this.tryApply();
        },
      });
    }
    return true;
  }

  /** Returns true when the new worker is in control (or none is involved). */
  private async prepareWorker(): Promise<boolean> {
    const w = this.deps.worker;
    if (!w) return true;
    let state = await w.status();
    if (state === "installing") {
      if (!(await w.waitUntilInstalled(this.deps.installTimeoutMs ?? 30_000))) return false;
      state = await w.status();
    }
    if (state === "waiting") {
      // Subscribe before messaging so a fast activation can't be missed.
      const controlled = w.waitForControllerChange(this.deps.activationTimeoutMs ?? 15_000);
      await w.requestActivation();
      return controlled;
    }
    return true;
  }

  private async apply(): Promise<void> {
    const target = this.pendingTarget;
    if (!target) return;
    const { storage } = this.deps;
    if (hasReloadedFor(storage, target)) return; // tried once already: never loop
    if (this.deferIfUnsafe(target)) return;

    const ready = await this.prepareWorker();
    if (!ready) return; // bounded wait expired: stay pending, no forced reload

    // A newer verified target may have arrived while we waited.
    const finalTarget = this.pendingTarget ?? target;
    if (finalTarget === this.deps.currentBuildId) return;
    if (this.deferIfUnsafe(finalTarget)) return; // user typed during activation
    if (hasReloadedFor(storage, finalTarget)) return;
    if (!markReloadedFor(storage, finalTarget)) return;

    this.reloaded = true;
    this.deps.announce(finalTarget);
    this.deps.reload();
  }
}
